import { Stack } from '@mantine/core';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { RevisionFieldProps } from 'components/forms/revision/fill-out/types';
import UploadFiles from 'components/files/upload/UploadFiles';
import P2Regular from 'components/typography/P2Regular';
import { IFormInputSpecFileList } from 'pages/revisions-module/template-editor/editors/form/types';
import { useEffect, useMemo } from 'react';
import type { IFileState } from 'components/files/upload/types';

export interface IStoredFile {
  fileId: string;
  fileName: string;
  fileType: string;
}

/**
 * File list input for the revision form.
 */
export default function RevisionFieldFileList({ spec, name }: RevisionFieldProps<IFormInputSpecFileList>) {
  const { form } = useFillOutRevisionDataProvider();
  const { value, onChange, error } = form.getInputProps(name);

  // Parse accept string to array format expected by UploadFiles
  const acceptArray = useMemo(() => {
    if (!spec.accept || spec.accept === '*/*') {
      return ['*'];
    }
    return spec.accept.split(',').map((type) => type.trim());
  }, [spec.accept]);

  // Initialize the form field if it doesn't exist
  useEffect(() => {
    if (value === undefined) {
      onChange([]);
    }
  }, [value]);

  /**
   * Handles file state changes and updates the form with finalized file data.
   */
  const handleChange = (files: IFileState[] | undefined) => {
    const storedFiles: IStoredFile[] = (files || [])
      .filter((file) => file.finalized && file.fileId)
      .map((file) => ({
        fileId: file.fileId!,
        fileName: file.fileName,
        fileType: file.fileType,
      }));

    onChange(storedFiles);
  };

  // Convert stored file data back to IFileState format for the UploadFiles component
  const initialFiles = useMemo(() => {
    const storedFiles = value as any as IStoredFile[] | undefined;

    if (!storedFiles || !Array.isArray(storedFiles)) {
      return [];
    }

    return storedFiles.map((file) => ({
      uuid: file.fileId,
      progress: 1,
      finalized: true,
      fileId: file.fileId,
      fileName: file.fileName,
      fileType: file.fileType,
    }));
  }, [value]);

  return (
    <Stack spacing={8}>
      <P2Regular color="gray.6">
        {spec.label} (max. {spec.maxFiles}){spec.required && <span style={{ color: 'red' }}> *</span>}
      </P2Regular>
      <UploadFiles
        accept={acceptArray}
        maxFiles={spec.maxFiles}
        onChange={handleChange}
        error={error}
        initialFiles={initialFiles}
        w="100%"
      />
    </Stack>
  );
}
